/**
 * Storage core: the schema, the connection, the change bus, and the plain CRUD.
 *
 * LAYER 1. This module knows how to READ and WRITE rows and nothing about what
 * they mean. Anything that enforces a rule about segments (a task-alive guard,
 * a renumber, "a blank correction is not a correction") lives in
 * `./segmentWrites.ts`; queue shapes live in `./queues.ts`; sweeps live in
 * `./maintenance.ts`. `src/db.ts` re-exports all four as one surface, so every
 * consumer still writes `from '../db'`.
 *
 * Object stores
 *   tasks    key = Task.id                 value = Task
 *   segments key = Segment.id              value = Segment
 *   audio    key = taskId                  value = Blob   (original uploaded/recorded file)
 *   clips    key = segmentId               value = Blob   (WAV slice cut by the app)
 *   logs     key = RunLog.id               value = RunLog (one first-pass run's telemetry)
 *
 * Indexes
 *   segments.by-task        -> taskId
 *   segments.by-task-index  -> [taskId, index]   (time-ordered reads)
 *   segments.by-task-verify -> [taskId, verify]
 *   segments.by-verify      -> verify            (cross-task queues)
 *   tasks.by-status         -> status
 *   tasks.by-createdAt      -> createdAt
 *   logs.by-task            -> taskId
 *   logs.by-startedAt       -> startedAt
 *
 * Blobs are kept in their own stores so a task/segment record stays cheap to
 * read; never inline audio into the Task/Segment values.
 *
 * THE ONE UPWARD IMPORT IN THIS LAYER is the `RunLog` type below. It is
 * type-only, erased at build time, zero runtime coupling — it exists because
 * `RunLog` is a STORED shape whose definition currently lives in a service.
 * Relocating it is follow-up F5. Do not add a value import from `services/`.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { RunLog } from '../services/pipelineLog';
import type { Segment, Task, TaskStatus, VerifyState } from '../types';

export const DB_NAME = 'lableforge';
/**
 * Schema version 3. The `logs` store (pipeline telemetry) is what versions 2
 * and 3 were both about.
 *
 * The upgrade is purely additive and every `createObjectStore` below is guarded
 * by a `contains` check, so an existing database keeps its tasks, segments,
 * audio and clips untouched: the upgrade transaction only creates whatever is
 * missing. Never drop or recreate a store here — users' recordings and their
 * human verification work live in this database and there is no server copy.
 *
 * Why 3 and not 2 — and why the guard below still says "v2": a development
 * database reached version 2 WITHOUT the `logs` store, because the version was
 * bumped in one edit and the store added in the next and a hot reload opened
 * the database in between. The `logs` guard is therefore labelled with the
 * version that was supposed to introduce it (2) while the constant is the
 * version that actually repairs it (3). Because the creates are guarded rather
 * than keyed to a version number, bumping again repairs that database and is a
 * no-op for any database that is already correct — which is the property this
 * upgrade function should have had all along. Deleting and rebuilding would
 * also have worked, and would have thrown away real recordings to save a
 * version number.
 *
 * ANY future change here must stay additive and contains-guarded. Never write a
 * migration that rewrites or drops stored rows.
 */
export const DB_VERSION = 3;

interface LableforgeDB extends DBSchema {
  tasks: {
    key: string;
    value: Task;
    indexes: {
      'by-status': TaskStatus;
      'by-createdAt': number;
    };
  };
  segments: {
    key: string;
    value: Segment;
    indexes: {
      'by-task': string;
      'by-task-index': [string, number];
      'by-task-verify': [string, VerifyState];
      'by-verify': VerifyState;
    };
  };
  audio: { key: string; value: Blob };
  clips: { key: string; value: Blob };
  logs: {
    key: string;
    value: RunLog;
    indexes: {
      'by-task': string;
      'by-startedAt': number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<LableforgeDB>> | null = null;

/** Open (and memoize) the database connection. Safe to call from anywhere. */
export function getDB(): Promise<IDBPDatabase<LableforgeDB>> {
  if (!dbPromise) {
    dbPromise = openDB<LableforgeDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('tasks')) {
          const tasks = db.createObjectStore('tasks', { keyPath: 'id' });
          tasks.createIndex('by-status', 'status');
          tasks.createIndex('by-createdAt', 'createdAt');
        }
        if (!db.objectStoreNames.contains('segments')) {
          const segments = db.createObjectStore('segments', { keyPath: 'id' });
          segments.createIndex('by-task', 'taskId');
          segments.createIndex('by-task-index', ['taskId', 'index']);
          segments.createIndex('by-task-verify', ['taskId', 'verify']);
          segments.createIndex('by-verify', 'verify');
        }
        if (!db.objectStoreNames.contains('audio')) db.createObjectStore('audio');
        if (!db.objectStoreNames.contains('clips')) db.createObjectStore('clips');
        // v2. Additive: an existing v1 database reaches here with its other four
        // stores already present and untouched.
        if (!db.objectStoreNames.contains('logs')) {
          const logs = db.createObjectStore('logs', { keyPath: 'id' });
          logs.createIndex('by-task', 'taskId');
          logs.createIndex('by-startedAt', 'startedAt');
        }
      },
    });
  }
  return dbPromise;
}
/* ------------------------------------------------------------------ */
/* Change notification                                                 */
/* ------------------------------------------------------------------ */

/**
 * Anything that writes tasks or segments fires this. The app shell listens so
 * the tab-bar badges stay honest while a page is doing the writing; pages that
 * snapshot a queue on mount can ignore it.
 *
 * Deliberately coarse — no payload, no per-store granularity. Listeners should
 * re-read whatever they need.
 */
export type DbChangeListener = () => void;

const changeListeners = new Set<DbChangeListener>();

/** Subscribe to writes. Returns an unsubscribe function. */
export function subscribeToDb(listener: DbChangeListener): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/** Fire the change signal. Exported so a bulk importer could batch its writes. */
export function emitDbChange(): void {
  for (const listener of changeListeners) {
    try {
      listener();
    } catch {
      // a broken listener must never take a write down with it
    }
  }
}

/** RFC4122-ish id, with a fallback for browsers without `crypto.randomUUID`. */
export function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ------------------------------------------------------------------ */
/* Tasks                                                               */
/* ------------------------------------------------------------------ */

/** Create and persist a task. Returns the stored record. */
export async function createTask(input: {
  name: string;
  durationSec: number;
  status?: TaskStatus;
}): Promise<Task> {
  const task: Task = {
    id: newId(),
    name: input.name,
    createdAt: Date.now(),
    status: input.status ?? 'new',
    durationSec: input.durationSec,
  };
  const db = await getDB();
  await db.put('tasks', task);
  emitDbChange();
  return task;
}

export async function putTask(task: Task): Promise<void> {
  const db = await getDB();
  await db.put('tasks', task);
  emitDbChange();
}

export async function getTask(id: string): Promise<Task | undefined> {
  const db = await getDB();
  return db.get('tasks', id);
}

/** All tasks, newest first. */
export async function listTasks(): Promise<Task[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('tasks', 'by-createdAt');
  return all.reverse();
}

/** Tasks in a given stage, newest first. */
export async function listTasksByStatus(status: TaskStatus): Promise<Task[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('tasks', 'by-status', status);
  return all.sort((a, b) => b.createdAt - a.createdAt);
}

/** Shallow-merge a patch into a task. No-op if the task is gone. */
export async function updateTask(id: string, patch: Partial<Omit<Task, 'id'>>): Promise<Task | undefined> {
  const db = await getDB();
  const tx = db.transaction('tasks', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    return undefined;
  }
  const next: Task = { ...existing, ...patch, id };
  await tx.store.put(next);
  await tx.done;
  emitDbChange();
  return next;
}

export function setTaskStatus(id: string, status: TaskStatus): Promise<Task | undefined> {
  return updateTask(id, { status });
}

/** Mark a task failed with a human-readable reason. */
export function setTaskError(id: string, error: string): Promise<Task | undefined> {
  return updateTask(id, { error });
}

/** Delete a task and every trace of it: segments, clips, original audio. */
export async function deleteTask(taskId: string): Promise<void> {
  const db = await getDB();
  const segmentIds = await db.getAllKeysFromIndex('segments', 'by-task', taskId);
  const logIds = await db.getAllKeysFromIndex('logs', 'by-task', taskId);
  const tx = db.transaction(['tasks', 'segments', 'audio', 'clips', 'logs'], 'readwrite');
  await Promise.all([
    tx.objectStore('tasks').delete(taskId),
    tx.objectStore('audio').delete(taskId),
    ...segmentIds.map((sid) => tx.objectStore('segments').delete(sid)),
    ...segmentIds.map((sid) => tx.objectStore('clips').delete(sid)),
    ...logIds.map((lid) => tx.objectStore('logs').delete(lid)),
  ]);
  await tx.done;
  emitDbChange();
  // TODO(E2): call decodedAudio.forget(taskId) here — the decoded AudioBuffer
  // for this task outlives its audio blob. Nothing invalidates that cache
  // today. Cannot be wired from here: db -> services is an upward import; the
  // fix is a change-bus subscription inside services/decodedAudio.ts. See F11.
}

/**
 * Drop every segment of a task and its clips, but KEEP the original audio and
 * the task row.
 *
 * This is what makes a first pass re-runnable: a retry needs the source audio
 * to still be there, so `deleteTask` is the wrong tool. Returns how many
 * segments were removed.
 */
export async function deleteSegmentsForTask(taskId: string): Promise<number> {
  const db = await getDB();
  const segmentIds = await db.getAllKeysFromIndex('segments', 'by-task', taskId);
  if (segmentIds.length === 0) return 0;
  const tx = db.transaction(['segments', 'clips'], 'readwrite');
  await Promise.all([
    ...segmentIds.map((sid) => tx.objectStore('segments').delete(sid)),
    ...segmentIds.map((sid) => tx.objectStore('clips').delete(sid)),
  ]);
  await tx.done;
  emitDbChange();
  return segmentIds.length;
}
/* ------------------------------------------------------------------ */
/* Segments                                                            */
/* ------------------------------------------------------------------ */

export async function putSegment(segment: Segment): Promise<void> {
  const db = await getDB();
  await db.put('segments', segment);
  emitDbChange();
}

/** Bulk-insert segments (one transaction) — used right after the first pass. */
export async function putSegments(segments: Segment[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('segments', 'readwrite');
  await Promise.all(segments.map((s) => tx.store.put(s)));
  await tx.done;
  emitDbChange();
}

export async function getSegment(id: string): Promise<Segment | undefined> {
  const db = await getDB();
  return db.get('segments', id);
}

/** All segments of a task in time order (by `index`). */
export async function getSegmentsByTask(taskId: string): Promise<Segment[]> {
  const db = await getDB();
  const segments = await db.getAllFromIndex('segments', 'by-task', taskId);
  return segments.sort((a, b) => a.index - b.index);
}

/** Segments of a task in a given verify state, in time order. */
export async function getSegmentsByTaskAndVerify(taskId: string, verify: VerifyState): Promise<Segment[]> {
  const db = await getDB();
  const segments = await db.getAllFromIndex('segments', 'by-task-verify', [taskId, verify] as [string, VerifyState]);
  return segments.sort((a, b) => a.index - b.index);
}

/** Shallow-merge a patch into a segment. No-op if the segment is gone. */
export async function updateSegment(
  id: string,
  patch: Partial<Omit<Segment, 'id' | 'taskId'>>,
): Promise<Segment | undefined> {
  const db = await getDB();
  const tx = db.transaction('segments', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    return undefined;
  }
  const next: Segment = { ...existing, ...patch, id, taskId: existing.taskId };
  await tx.store.put(next);
  await tx.done;
  emitDbChange();
  return next;
}
/* ------------------------------------------------------------------ */
/* Blobs: original audio + per-segment clips                           */
/* ------------------------------------------------------------------ */

export async function putTaskAudio(taskId: string, blob: Blob): Promise<void> {
  const db = await getDB();
  await db.put('audio', blob, taskId);
  // TODO(E2): call decodedAudio.forget(taskId) here — replacing the blob leaves
  // a decoded buffer of the OLD audio cached under this id. See F11.
}

export async function getTaskAudio(taskId: string): Promise<Blob | undefined> {
  const db = await getDB();
  return db.get('audio', taskId);
}

export async function deleteTaskAudio(taskId: string): Promise<void> {
  const db = await getDB();
  await db.delete('audio', taskId);
}

/* ------------------------------------------------------------------ */
/* Pipeline run logs                                                   */
/* ------------------------------------------------------------------ */

/**
 * How many run logs a task keeps. Each is a few hundred KB at most (the raw
 * Gemini response dominates), and the useful history is "this run and the one
 * before it" — older ones are dropped oldest-first.
 */
const MAX_LOGS_PER_TASK = 5;

/** Store a run log, trimming the task's history. Never part of a user-visible flow. */
export async function putRunLog(log: RunLog): Promise<void> {
  const db = await getDB();
  await db.put('logs', log);
  const existing = await db.getAllFromIndex('logs', 'by-task', log.taskId);
  if (existing.length > MAX_LOGS_PER_TASK) {
    const doomed = existing
      .sort((a, b) => a.startedAt - b.startedAt)
      .slice(0, existing.length - MAX_LOGS_PER_TASK);
    const tx = db.transaction('logs', 'readwrite');
    await Promise.all(doomed.map((l) => tx.store.delete(l.id)));
    await tx.done;
  }
}

/** Every run log for a task, newest first. */
export async function getRunLogsByTask(taskId: string): Promise<RunLog[]> {
  const db = await getDB();
  const logs = await db.getAllFromIndex('logs', 'by-task', taskId);
  return logs.sort((a, b) => b.startedAt - a.startedAt);
}

/** The most recent run log for a task, if it has one. */
export async function getLatestRunLog(taskId: string): Promise<RunLog | undefined> {
  return (await getRunLogsByTask(taskId))[0];
}

/**
 * Ids of tasks that have at least one run log.
 *
 * Cheap enough to call on every Tasks-tab refresh: it reads the index keys, not
 * the log values, so the (large) raw Gemini responses never leave the store.
 */
export async function listTasksWithLogs(): Promise<Set<string>> {
  const db = await getDB();
  const out = new Set<string>();
  const tx = db.transaction('logs', 'readonly');
  // A KEY cursor over the index: this yields the indexed value (the taskId) and
  // never deserialises the record, so the large raw Gemini responses stay put.
  // `nextunique` visits each distinct taskId once rather than once per run.
  let cursor = await tx.store.index('by-task').openKeyCursor(null, 'nextunique');
  while (cursor) {
    out.add(cursor.key);
    cursor = await cursor.continue();
  }
  await tx.done;
  return out;
}

export async function deleteRunLogsForTask(taskId: string): Promise<void> {
  const db = await getDB();
  const ids = await db.getAllKeysFromIndex('logs', 'by-task', taskId);
  if (ids.length === 0) return;
  const tx = db.transaction('logs', 'readwrite');
  await Promise.all(ids.map((id) => tx.store.delete(id)));
  await tx.done;
}

/* ------------------------------------------------------------------ */
/* Clips                                                               */
/* ------------------------------------------------------------------ */

export async function putClip(segmentId: string, blob: Blob): Promise<void> {
  const db = await getDB();
  await db.put('clips', blob, segmentId);
}

/** Bulk-store clip WAVs (one transaction) — used by the slicer. */
export async function putClips(entries: Array<{ segmentId: string; blob: Blob }>): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('clips', 'readwrite');
  await Promise.all(entries.map((e) => tx.store.put(e.blob, e.segmentId)));
  await tx.done;
}

export async function getClip(segmentId: string): Promise<Blob | undefined> {
  const db = await getDB();
  return db.get('clips', segmentId);
}

export async function deleteClip(segmentId: string): Promise<void> {
  const db = await getDB();
  await db.delete('clips', segmentId);
}
